import { FilterDropdown } from "@/components/Filter/FilterDropdown";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAddLibraryFilesToKnowledgeMutation } from "@/data/use-add-library-to-knowledge-data";
import { useKitFoldersData } from "@/data/use-kit-folders-data";
import {
  useLibraryFilesData,
  type FilesFilters,
} from "@/data/use-library-files-data";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { smartDate } from "@/lib/date";
import {
  FILE_SOURCE_FILTER_OPTIONS,
  getFileSourceLabel,
  type FileSourceFilter,
} from "@/lib/file-labels";
import type { KitFolderDto } from "@/lib/gen-api";
import { canWriteArtifact } from "@/lib/grant";
import { cn } from "@/lib/utils";
import { File } from "@/types/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

interface AddFromLibraryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupUuid: string;
  kitUuid: string;
  defaultFolderUuid?: string | null;
}

function buildFolderLabel(
  foldersByUuid: Record<string, KitFolderDto>,
  folderUuid: string,
): string {
  const segments: string[] = [];
  let currentUuid: string | null = folderUuid;

  while (currentUuid) {
    const currentFolder: KitFolderDto | null =
      currentUuid in foldersByUuid ? foldersByUuid[currentUuid] : null;
    if (!currentFolder) {
      break;
    }

    segments.unshift(currentFolder.name);
    currentUuid = currentFolder.parentFolderUuid;
  }

  return segments.join(" / ");
}

export function AddFromLibraryDialog({
  open,
  onOpenChange,
  groupUuid,
  kitUuid,
  defaultFolderUuid = null,
}: AddFromLibraryDialogProps) {
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<FileSourceFilter>(null);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [folderUuid, setFolderUuid] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(searchInput, 300);

  const filters: FilesFilters = useMemo(
    () => ({
      search: debouncedSearch,
      category: categoryFilter,
    }),
    [categoryFilter, debouncedSearch],
  );

  const { folders } = useKitFoldersData(groupUuid, kitUuid);
  const {
    libraryFiles,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useLibraryFilesData(groupUuid, kitUuid, filters);

  const addMutation = useAddLibraryFilesToKnowledgeMutation(groupUuid, kitUuid);

  const foldersByUuid = useMemo(
    () => Object.fromEntries(folders.map((folder) => [folder.uuid, folder])),
    [folders],
  );

  const selectableFolders = useMemo(
    () =>
      folders
        .filter(
          (folder) =>
            canWriteArtifact(folder) && folder.externalProvider === null,
        )
        .sort((left, right) =>
          buildFolderLabel(foldersByUuid, left.uuid).localeCompare(
            buildFolderLabel(foldersByUuid, right.uuid),
          ),
        ),
    [folders, foldersByUuid],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setSearchInput("");
    setCategoryFilter(null);
    setSelectedUuids(new Set());
    setFolderUuid(defaultFolderUuid ?? null);
  }, [defaultFolderUuid, open]);

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element || !hasNextPage || !open) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, open]);

  const allVisibleSelected =
    libraryFiles.length > 0 &&
    libraryFiles.every((file) => selectedUuids.has(file.uuid));
  const someVisibleSelected = libraryFiles.some((file) =>
    selectedUuids.has(file.uuid),
  );

  function toggleFile(fileUuid: string, checked: boolean) {
    setSelectedUuids((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(fileUuid);
      } else {
        next.delete(fileUuid);
      }
      return next;
    });
  }

  function toggleAllVisible(checked: boolean) {
    setSelectedUuids((current) => {
      const next = new Set(current);
      for (const file of libraryFiles) {
        if (checked) {
          next.add(file.uuid);
        } else {
          next.delete(file.uuid);
        }
      }
      return next;
    });
  }

  async function handleSubmit() {
    if (!folderUuid) {
      toast.error("Select a knowledge folder");
      return;
    }

    if (selectedUuids.size === 0) {
      toast.error("Select at least one library file");
      return;
    }

    try {
      const result = await addMutation.mutateAsync({
        fileUuids: [...selectedUuids],
        folderUuid,
      });

      if (result.failed > 0) {
        toast.success(
          `Added ${result.added} file${result.added === 1 ? "" : "s"} to knowledge. ${result.failed} could not be added.`,
        );
      } else {
        toast.success(
          `Added ${result.added} file${result.added === 1 ? "" : "s"} to knowledge — parsing will begin shortly`,
        );
      }

      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to add files to knowledge";
      toast.error(message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Add from Library</DialogTitle>
          <DialogDescription>
            Copy files from your workbook library into a knowledge folder so
            they can be queried by AI.
          </DialogDescription>
        </DialogHeader>

        <Column className="min-h-0 flex-1 gap-4">
          <Row className="flex-wrap items-end gap-3">
            <Column className="min-w-[220px] flex-1 gap-2">
              <p className="text-sm font-medium">Knowledge folder</p>
              <Select
                value={folderUuid ?? undefined}
                onValueChange={(value) => setFolderUuid(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  {selectableFolders.map((folder) => (
                    <SelectItem key={folder.uuid} value={folder.uuid}>
                      {buildFolderLabel(foldersByUuid, folder.uuid)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Column>
          </Row>

          <Row className="flex-wrap items-center gap-3">
            <Input
              value={searchInput}
              placeholder="Search library files"
              className="max-w-sm"
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <FilterDropdown
              label="Type"
              options={FILE_SOURCE_FILTER_OPTIONS.map((option) => option.value)}
              value={categoryFilter}
              onChange={setCategoryFilter}
              getLabel={(value) =>
                value
                  ? (FILE_SOURCE_FILTER_OPTIONS.find(
                      (option) => option.value === value,
                    )?.label ?? "All types")
                  : "All types"
              }
              allLabel="All types"
              className="border-border"
            />
            {libraryFiles.length > 0 ? (
              <p className="text-muted-foreground ml-auto text-sm">
                {selectedUuids.size} selected
                {total > 0 ? ` · ${libraryFiles.length} of ${total} shown` : ""}
              </p>
            ) : null}
          </Row>

          <div className="min-h-0 flex-1 overflow-auto rounded-md border">
            {isLoading ? (
              <p className="text-muted-foreground p-4 text-sm">
                Loading library…
              </p>
            ) : libraryFiles.length === 0 ? (
              <p className="text-muted-foreground p-4 text-sm">
                No library files match your search.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={
                          allVisibleSelected
                            ? true
                            : someVisibleSelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={(checked) =>
                          toggleAllVisible(checked === true)
                        }
                        aria-label="Select all visible files"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-40">Type</TableHead>
                    <TableHead className="w-36">Added</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {libraryFiles.map((file: File) => {
                    const isSelected = selectedUuids.has(file.uuid);

                    return (
                      <TableRow
                        key={file.uuid}
                        className={cn(isSelected && "bg-muted/40")}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) =>
                              toggleFile(file.uuid, checked === true)
                            }
                            aria-label={`Select ${file.name}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {file.name}
                        </TableCell>
                        <TableCell>{getFileSourceLabel(file.source)}</TableCell>
                        <TableCell>{smartDate(file.dateCreated)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            <div ref={loadMoreRef} className="flex justify-center py-3">
              {isFetchingNextPage ? (
                <p className="text-muted-foreground text-sm">Loading more…</p>
              ) : null}
            </div>
          </div>
        </Column>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              !folderUuid ||
              selectedUuids.size === 0 ||
              addMutation.isPending ||
              selectableFolders.length === 0
            }
            onClick={() => void handleSubmit()}
          >
            {addMutation.isPending
              ? "Adding…"
              : `Add ${selectedUuids.size || ""} to knowledge`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
